import * as React from 'react'
import { Box, Link } from '@mui/material'
import { styled } from '@mui/material/styles'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'

import * as d3 from 'd3'
import { IVariantDetailClinvarGraph } from '@/@core/services/app.service'
import { useTranslation } from 'react-i18next'
import { ExternalLinks } from '@/@core/services/api'

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '36px !important'
  }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={null} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '36px !important'
  },
  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
    marginLeft: theme.spacing(1)
  }
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)'
}))

const BoTree = (props: { data: IVariantDetailClinvarGraph[] | [] }) => {
  const data = props.data.map((item) => {
    return {
      title: item.clinsig,
      value: `${item.chrom}:${item.key}`,
      variant_id: item.variant_id
    }
  })

  data.push({
    title: '',
    value: '',
    variant_id: ''
  })

  data.push({
    title: '',
    value: '',
    variant_id: ''
  })

  data.unshift({
    title: '',
    value: '',
    variant_id: ''
  })

  data.unshift({
    title: '',
    value: '',
    variant_id: ''
  })

  React.useEffect(() => {
    const dom = document.getElementById('container')
    const index = props.data.findIndex((item) => item.bold) + 2

    if (dom) {
      const width = dom.clientWidth
      const height = dom.clientHeight
      const marginTop = 0
      const marginRight = 0
      const marginBottom = 0
      const marginLeft = (dom.clientWidth / data.length - 1) * 2

      const svg = d3.create('svg').attr('width', width).attr('height', height)

      const x = d3.scaleLinear(
        [0, data.length - 1],
        [marginLeft, width - marginRight]
      )

      const xAxis = d3
        .axisBottom(x)
        .ticks(data.length)
        .tickFormat((d) => {
          return data[d as any].value
        })

      //
      const flag = data.some((item) => item.title.length >= 25)

      const yAxis = d3
        .axisTop(x)
        .ticks(data.length)
        .tickFormat((d) => {
          return data[d as any].title
        })

      const xg = svg
        .append('g')
        .attr(
          'transform',
          `translate(0,${(height - marginBottom) / (flag ? 2 : 3.5)})`
        )
        .attr('id', `xg`)
        .call(xAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .style('font-size', '0.75rem')
        .style('color', '#32B0C9')

        // .transition()
        // .duration(750)
        .attr('transform', 'rotate(310)')
        .call((g) => {
          g.nodes().map((item: any, i) => {
            if (i === index) {
              item.style.fontWeight = 'bold'

              item.style.fontSize = '0.9rem'
            }
          })
        })

      const yg = svg
        .append('g')
        .attr(
          'transform',
          `translate(0,${(height - marginBottom) / (flag ? 2 : 3.5)})`
        )
        .attr('padding-right', '20px')
        .attr('id', `yg`)
        .call(yAxis)
        .selectAll('text')
        .style('cursor', 'pointer')
        .style('text-anchor', 'end')
        .style('font-size', '0.75rem')
        .style('color', '#32B0C9')

        // .transition()
        // .duration(750)
        .attr('transform', 'rotate(50)')
        .call((g) => {
          g.nodes().map((item: any, i) => {
            if (i === index) {
              item.style.fontWeight = 'bold'

              item.style.fontSize = '0.9rem'
            }

            if (item.innerHTML === 'Pathogenic') {
              item.style.color = 'rgb(232,72,56)'

              item.style.fontWeight = 'bold'
            }

            if (item.innerHTML === 'Likely_pathogenic') {
              item.style.color = 'rgb(225,135,74)'

              item.style.fontWeight = 'bold'
            }

            if (item.innerHTML === 'Pathogenic/Likely_pathogenic') {
              item.style.color = 'rgb(232,72,56)'

              item.style.fontWeight = 'bold'
            }

            item.addEventListener('click', () => {
              window.open(
                `${ExternalLinks.ncbi.variation}/${data[i].variant_id}`
              )
            })
          })
        })
      const nodes = dom.children

      Array.from(nodes).map((item) => dom.removeChild(item))

      dom.append(svg.node() as SVGSVGElement)
      const xgDom = document.getElementById(
        'xg'
      ) as unknown as SVGGraphicsElement
      const ygDom = document.getElementById(
        'yg'
      ) as unknown as SVGGraphicsElement
      const xgParams = xgDom.getBBox()
      const ygParams = ygDom.getBBox()

      svg.attr('viewBox', [
        0,
        0,
        Math.max(xgParams.width, ygParams.width),
        height
      ])
    }
  })

  return (
    <Box maxHeight={'500px'} overflow="hidden">
      <div id="container" style={{ width: '100%', height: '500px' }}></div>
    </Box>
  )
}

const ClinvarDiagram = React.forwardRef(
  (
    props: {
      value: IVariantDetailClinvarGraph[] | []
    },
    ref
  ) => {
    const { t } = useTranslation()
    const data = [
      {
        title: 'CLINVAR GRAPH'
      }
    ]

    if (!props.value.length)
      return (
        <Box>
          {data.map((items) => {
            return (
              <Accordion key={items.title} expanded={true}>
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  sx={{
                    cursor: 'default !important'
                  }}
                >
                  <Typography
                    sx={{
                      '::before': {
                        content: '""',
                        display: 'inline-block',
                        width: 4,
                        height: '1.25rem',
                        bgcolor: 'primary.main',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: '-10px'
                      },
                      lineHeight: '1.25em',
                      position: 'relative',
                      ml: '10px'
                    }}
                  >
                    {items.title}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Box padding={'10px 5px'}>
                    <Typography textAlign={'center'}>
                      {t('No Results')}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      )

    return (
      <Box>
        {data.map((items) => {
          return (
            <Accordion key={items.title} expanded={true}>
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                sx={{
                  cursor: 'default !important'
                }}
              >
                <Typography
                  sx={{
                    '::before': {
                      content: '""',
                      display: 'inline-block',
                      width: 4,
                      height: '1.25rem',
                      bgcolor: 'primary.main',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      left: '-10px'
                    },
                    lineHeight: '1.25em',
                    position: 'relative',
                    ml: '10px'
                  }}
                >
                  {items.title}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                {/* <Typography padding={'10px 5px'}>{items.description}</Typography> */}
                <BoTree data={props.value} />
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    )
  }
)

export default ClinvarDiagram
